import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAppearance } from "../../contexts/AppearanceContext";
import { Clock, AlertTriangle, DollarSign, TrendingDown, Loader2, Download, ArrowUpDown } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RT, ResponsiveContainer, Cell } from "recharts";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUCKET_COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#F97316", "#EF4444"];

function KpiCard({ icon: Icon, title, value, sub, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "18" }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{title}</p>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value ?? "—"}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AgingReport({ filterHook }) {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState("days_stagnated");
  const [sortDir, setSortDir] = useState("desc");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = filterHook.buildApiFilters();
      const res = await axios.post(`${API}/reports/aging`, { filters }, { withCredentials: true });
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [filterHook]);

  useEffect(() => { fetchData(); }, [filterHook.filters.pipelineIds, filterHook.filters.responsavelIds]); // eslint-disable-line

  const toggleSort = (f) => { if (sortField === f) setSortDir(d => d === "desc" ? "asc" : "desc"); else { setSortField(f); setSortDir("desc"); } };
  const sorted = (list) => [...list].sort((a, b) => sortDir === "desc" ? (b[sortField] || 0) - (a[sortField] || 0) : (a[sortField] || 0) - (b[sortField] || 0));

  const exportExcel = () => {
    if (!data?.deals) return;
    const ws = XLSX.utils.json_to_sheet(data.deals.map(d => ({
      "Negócio": d.titulo, "Pipeline": d.pipeline_name, "Etapa": d.stage_name,
      "Responsável": d.responsavel, "Dias Parado": d.days_stagnated,
      "MRR": d.valor_mrr, "Última Atividade": d.last_activity ? new Date(d.last_activity).toLocaleDateString("pt-BR") : "—",
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Aging");
    XLSX.writeFile(wb, `aging_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading && !data) return <div className="flex items-center justify-center h-48 text-slate-400 text-sm"><Loader2 className="w-5 h-5 animate-spin mr-2" />Carregando...</div>;
  if (!data) return <p className="text-sm text-slate-400">Sem dados.</p>;

  const { totals, deals, by_stage, buckets } = data;
  const SortH = ({ field, children }) => (
    <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 whitespace-nowrap" onClick={() => toggleSort(field)}>
      <span className="inline-flex items-center gap-0.5">{children}<ArrowUpDown className={`w-2.5 h-2.5 ${sortField === field ? "text-slate-700" : "text-slate-300"}`} /></span>
    </th>
  );

  const getDaysColor = (days) => days > 60 ? "text-rose-600 font-bold" : days > 30 ? "text-orange-500 font-semibold" : days > 15 ? "text-amber-500" : "text-slate-600";

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Clock} title="Negócios Ativos" value={totals.total_deals} sub="em aberto" color={tc500} />
        <KpiCard icon={AlertTriangle} title="Média de Dias" value={`${totals.avg_days}d`} sub="parados na etapa" color="#F59E0B" />
        <KpiCard icon={TrendingDown} title="Estagnados 30d+" value={totals.stagnated_30plus} sub="negócios parados" color="#EF4444" />
        <KpiCard icon={DollarSign} title="MRR em Risco" value={`R$ ${(totals.total_mrr_at_risk || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} sub="parados há 30+ dias" color="#EF4444" />
      </div>

      {/* Charts: Buckets + By Stage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Clock className="w-4 h-4" style={{ color: tc500 }} />Distribuição por Tempo</h3>
          {buckets?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={buckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <RT contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" name="Negócios" radius={[4, 4, 0, 0]}>
                  {buckets.map((_, i) => <Cell key={i} fill={BUCKET_COLORS[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-8">Sem dados</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" style={{ color: "#F59E0B" }} />Média de Dias por Etapa</h3>
          {by_stage?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={by_stage} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10 }} unit="d" />
                <YAxis type="category" dataKey="stage" tick={{ fontSize: 10 }} width={100} />
                <RT contentStyle={{ fontSize: 12 }} formatter={(v) => `${v} dias`} />
                <Bar dataKey="avg_days" name="Média dias" fill="#F59E0B" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-8">Sem dados</p>}
        </div>
      </div>

      {/* Deals Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="text-sm font-semibold text-slate-700">Negócios Estagnados</h3>
          <Button variant="outline" size="sm" onClick={exportExcel} className="gap-2 text-slate-600 h-8 text-xs"><Download className="w-3.5 h-3.5" />Exportar</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-y border-slate-100 bg-slate-50/50">
              <tr>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Negócio</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Etapa</th>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Responsável</th>
                <SortH field="days_stagnated">Dias Parado</SortH>
                <SortH field="valor_mrr">MRR</SortH>
                <th className="px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Última Mov.</th>
              </tr>
            </thead>
            <tbody>
              {sorted(deals).map(d => (
                <tr key={d.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-xs font-medium text-slate-800 max-w-[180px] truncate">{d.titulo}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">{d.stage_name}</Badge></td>
                  <td className="px-3 py-2 text-xs text-slate-600">{d.responsavel}</td>
                  <td className={`px-3 py-2 text-xs text-center ${getDaysColor(d.days_stagnated)}`}>{d.days_stagnated}d</td>
                  <td className="px-3 py-2 text-xs text-slate-600 text-right">R$ {(d.valor_mrr || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-[10px] text-slate-400">{d.last_activity ? new Date(d.last_activity).toLocaleDateString("pt-BR") : "—"}</td>
                </tr>
              ))}
              {deals.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-slate-400">Nenhum negócio em aberto.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
